import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../config';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DetailComponents.css';

const CreateProposalModal = ({ onSave, onClose, isEditing = false, existingProposal = null }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [randomId] = useState(() => Math.random().toString(36).substr(2, 9));
  const [formData, setFormData] = useState({
    title: '',
    notes: '',
    clientId: '',
    startDate: null,
    endDate: null,
    destinations: [''],
    numberOfTravelers: '',
    numberOfRooms: ''  });

  // Pre-populate form data when editing
  useEffect(() => {
    if (isEditing && existingProposal) {
      setFormData({
        title: existingProposal.title || '',
        notes: existingProposal.description || '',
        clientId: existingProposal.clientId ? existingProposal.clientId._id : '',
        startDate: existingProposal.startDate ? new Date(existingProposal.startDate) : null,
        endDate: existingProposal.endDate ? new Date(existingProposal.endDate) : null,
        destinations: existingProposal.destinations && existingProposal.destinations.length > 0
          ? existingProposal.destinations
          : [''],
        numberOfTravelers: existingProposal.numberOfTravelers || '',
        numberOfRooms: existingProposal.numberOfRooms || ''
      });

      // Pre-populate client search query
      if (existingProposal.clientId) {
        setClientSearchQuery(`${existingProposal.clientId.name} ${existingProposal.clientId.lastName1}`);
      }
    }
  }, [isEditing, existingProposal]);
  
  const { user } = useContext(AuthContext);
  const modalRef = useRef(null);
  const clientDropdownRef = useRef(null);

  const handleClickOutside = useCallback((event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  // Separate effect for dropdown click outside
  useEffect(() => {
    const handleDropdownClickOutside = (event) => {
      if (showClientDropdown && clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        const clientInput = document.getElementById(`client-search-${randomId}`);
        if (clientInput && !clientInput.contains(event.target)) {
          setShowClientDropdown(false);
          setHighlightedIndex(-1);
        }
      }
    };

    if (showClientDropdown) {
      document.addEventListener('mousedown', handleDropdownClickOutside);
      return () => document.removeEventListener('mousedown', handleDropdownClickOutside);
    }
  }, [showClientDropdown, randomId]);

  useEffect(() => {
    fetchClients();
  }, []);

  // Filter clients based on search query
  useEffect(() => {
    if (clientSearchQuery.trim() === '') {
      setClientSearchResults([]);
      setShowClientDropdown(false);
      setHighlightedIndex(-1);
    } else {
      const filtered = clients.filter(client =>
        `${client.name} ${client.lastName1}`.toLowerCase().includes(clientSearchQuery.toLowerCase())
      );
      setClientSearchResults(filtered);
      setShowClientDropdown(filtered.length > 0);
      setHighlightedIndex(filtered.length > 0 ? 0 : -1);
    }
  }, [clientSearchQuery, clients]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clients`);
      setClients(response.data.data || []);
    } catch (err) {
      setError('Failed to load clients');
      console.error('Error fetching clients:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleClientSearchChange = (e) => {
    setClientSearchQuery(e.target.value);
  };

  const handleClientSelect = (client) => {
    setFormData({ ...formData, clientId: client._id });
    setClientSearchQuery(`${client.name} ${client.lastName1}`);
    // Force close dropdown immediately
    setShowClientDropdown(false);
    setHighlightedIndex(-1);
    // Additional cleanup after a brief delay
    setTimeout(() => {
      setShowClientDropdown(false);
      setHighlightedIndex(-1);
    }, 10);
  };

  const handleClientClear = () => {
    setFormData({ ...formData, clientId: '' });
    setClientSearchQuery('');
    setShowClientDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleClientKeyDown = (e) => {
    if (!showClientDropdown || clientSearchResults.length === 0) {
      // If no dropdown is shown, allow normal input behavior
      if (e.key === 'ArrowDown' && clientSearchQuery.trim()) {
        // Show dropdown if there are results
        setShowClientDropdown(true);
        setHighlightedIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < clientSearchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : clientSearchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < clientSearchResults.length) {
          handleClientSelect(clientSearchResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowClientDropdown(false);
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setShowClientDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleDestinationChange = (index, value) => {
    const newDestinations = [...formData.destinations];
    newDestinations[index] = value;
    setFormData({ ...formData, destinations: newDestinations });
  };

  const addDestination = (newDestination = '') => {
    setFormData({
      ...formData,
      destinations: [...formData.destinations, newDestination]
    });
  };

  const removeDestination = (index) => {
    if (formData.destinations.length > 1) {
      const newDestinations = formData.destinations.filter((_, i) => i !== index);
      setFormData({ ...formData, destinations: newDestinations });
    }
  };

  const handleStartDateChange = (date) => {
    setFormData({ ...formData, startDate: date });
  };

  const handleEndDateChange = (date) => {
    setFormData({ ...formData, endDate: date });
  };

  const validateForm = () => {
    if (!formData.title || !formData.clientId || !formData.startDate || !formData.endDate) {
      setError('Please fill in all required fields');
      return false;
    }

    // Check if at least one destination is filled
    const hasValidDestination = formData.destinations.some(dest => dest.trim() !== '');
    if (!hasValidDestination) {
      setError('Please add at least one destination');
      return false;
    }

    if (formData.endDate < formData.startDate) {
      setError('End date cannot be before start date');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      const filteredDestinations = formData.destinations.filter(dest => dest.trim() !== '');
      const proposalData = {
        ...formData,
        destinations: filteredDestinations, // Filter out empty destinations
        description: formData.notes, // Map notes to description field
        startDate: formData.startDate.toISOString(),
        endDate: formData.endDate.toISOString()
      };

      // Remove notes field since we use description
      delete proposalData.notes;

      let response;
      if (isEditing && existingProposal) {
        // Update existing proposal
        response = await axios.put(`${API_BASE_URL}/api/proposals/${existingProposal._id}`, proposalData);
      } else {
        // Create new proposal
        proposalData.createdBy = user.userId;
        response = await axios.post(`${API_BASE_URL}/api/proposals`, proposalData);
      }

      onSave(response.data.data);
      onClose();
    } catch (err) {
      setError(isEditing ? 'Failed to update proposal' : 'Failed to create proposal');
      console.error(isEditing ? 'Error updating proposal:' : 'Error creating proposal:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal edit-modal" ref={modalRef}>
        <h3>{isEditing ? 'Edit Trip Proposal' : 'Create Trip Proposal'}</h3>
        <form autoComplete="off" style={{ display: 'contents' }}>
          <div className="edit-modal-form">
          {error && (
            <div style={{ 
              color: 'red', 
              marginBottom: '15px', 
              padding: '10px', 
              backgroundColor: '#f8d7da', 
              border: '1px solid #f5c6cb', 
              borderRadius: '3px' 
            }}>
              {error}
            </div>
          )}

          <div className="edit-modal-field">
            <label className="edit-modal-label">Proposal Title *</label>
            <input
              className="edit-modal-input"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Summer Vacation to Hawaii"
              required
              autoComplete="off"
            />
          </div>
          
          <div className="edit-modal-field">
            <label className="edit-modal-label">Client *</label>
            <div style={{ position: 'relative', width: '100%'}}>
              {/* Hidden fake input to trick autofill */}
              <input
                type="text"
                style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
                autoComplete="username"
                tabIndex={-1}
              />
              <input
                className="edit-modal-input"
                type="text"
                value={clientSearchQuery}
                onChange={handleClientSearchChange}
                onKeyDown={handleClientKeyDown}
                placeholder="Search and select a client..."
                required
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                name={`client-search-${randomId}`}
                id={`client-search-${randomId}`}
                readOnly={false}
                onFocus={(e) => {
                  e.target.removeAttribute('readonly');
                  if (clientSearchQuery.trim() && clientSearchResults.length > 0) {
                    setShowClientDropdown(true);
                  }
                }}
                onMouseEnter={(e) => {
                  e.target.removeAttribute('readonly');
                }}
              />
              {formData.clientId && (
                <button
                  type="button"
                  onClick={handleClientClear}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6c757d',
                    fontSize: '16px',
                    padding: '4px'
                  }}
                  title="Clear selection"
                >
                  ×
                </button>
              )}
              {showClientDropdown && clientSearchResults.length > 0 && (
                <div 
                  ref={clientDropdownRef}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                  {clientSearchResults.map((client, index) => (
                    <div
                      key={client._id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleClientSelect(client);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        backgroundColor: highlightedIndex === index ? '#e3f2fd' : 'white',
                        hover: { backgroundColor: '#f8f9fa' }
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        setHighlightedIndex(index);
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = highlightedIndex === index ? '#e3f2fd' : 'white';
                      }}
                    >
                      {client.name} {client.lastName1}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="edit-modal-field">
            <label className="edit-modal-label">Destinations</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <input
                className="edit-modal-input"
                type="text"
                placeholder="Enter destination (e.g., Hawaii, Paris, Tokyo)"
                style={{ flex: 1 }}
                autoComplete="off"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = e.target.value.trim();
                    if (value) {
                      addDestination(value);
                      e.target.value = '';
                    }
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const input = document.querySelector('input[placeholder*="Enter destination"]');
                  const value = input?.value.trim();
                  if (value) {
                    addDestination(value);
                    input.value = '';
                  }
                }}
                className="add-button"
                title="Add destination"
              >
                +
              </button>
            </div>

            {/* Display added destinations */}
            {formData.destinations.filter(dest => dest.trim() !== '').length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {formData.destinations
                  .map((dest, index) => dest.trim() !== '' ? (
                    <span key={index} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginRight: '8px',
                      marginBottom: '4px',
                      padding: '2px 6px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '3px',
                      fontSize: '0.8em'
                    }}>
                      {dest}
                      <button
                        type="button"
                        onClick={() => removeDestination(index)}
                        style={{
                          marginLeft: '4px',
                          padding: '0 2px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#6c757d',
                          cursor: 'pointer',
                          fontSize: '0.9em',
                          lineHeight: 1
                        }}
                        title="Remove destination"
                      >
                        ×
                      </button>
                    </span>
                  ) : null)
                  .filter(Boolean)
                }
              </div>
            )}
          </div>

          <div className="edit-modal-field">
            <label className="edit-modal-label">Number of Travelers</label>
            <input
              className="edit-modal-input"
              type="number"
              name="numberOfTravelers"
              value={formData.numberOfTravelers}
              onChange={handleChange}
              min="1"
              max="20"
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  e.preventDefault();
                }
              }}
            />
          </div>

          <div className="edit-modal-field">
            <label className="edit-modal-label">Number of Rooms</label>
            <input
              className="edit-modal-input"
              type="number"
              name="numberOfRooms"
              value={formData.numberOfRooms}
              onChange={handleChange}
              min="1"
              max="10"
              autoComplete="off"
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  e.preventDefault();
                }
              }}
            />
          </div>

          <div className="edit-modal-field">
            <label className="edit-modal-label">Start Date *</label>
            <DatePicker
              selected={formData.startDate}
              onChange={handleStartDateChange}
              dateFormat="MMMM d, yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              placeholderText="Select start date"
              className="edit-modal-input"
              required
            />
          </div>
          
          <div className="edit-modal-field">
            <label className="edit-modal-label">End Date *</label>
            <DatePicker
              selected={formData.endDate}
              onChange={handleEndDateChange}
              dateFormat="MMMM d, yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              minDate={formData.startDate}
              placeholderText="Select end date"
              className="edit-modal-input"
              required
            />
          </div>
          
          <div className="edit-modal-field">
            <label className="edit-modal-label">Notes</label>
            <textarea
              className="edit-modal-input"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Optional notes for the proposal..."
              rows="4"
              style={{ resize: 'vertical' }}
            />
          </div>
        </div>
        </form>
        
        <div className="modal-actions">
          <button className="modal-action-button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="modal-action-button"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProposalModal;
